const STORAGE_KEY = 'island-recent-searches';
const MAX_RECENT = 6;

/**
 * @param {ParentNode} [root]
 * @returns {HTMLElement | null}
 */
function getModal(root = document) {
  return root.querySelector('#search-modal');
}

/**
 * @returns {string[]}
 */
function readRecentSearches() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((term) => String(term).trim())
      .filter(Boolean)
      .slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

/**
 * @param {string} term
 */
function saveRecentSearch(term) {
  const normalized = term.trim();
  if (!normalized) return;

  const next = [normalized, ...readRecentSearches().filter((item) => item.toLowerCase() !== normalized.toLowerCase())].slice(
    0,
    MAX_RECENT
  );

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Ignore storage errors (private mode, quota, etc.)
  }
}

/**
 * @param {HTMLElement} modal
 */
function renderRecentSearches(modal) {
  const panel = modal.querySelector('[data-recent-searches-panel]');
  const list = modal.querySelector('[data-recent-searches]');
  if (!panel || !list) return;

  const terms = readRecentSearches();
  const current = Array.from(list.querySelectorAll('[data-search-term]')).map((el) => el.dataset.searchTerm);
  const isHidden = panel.hasAttribute('hidden');

  if (current.join('\0') === terms.join('\0') && isHidden === (terms.length === 0)) {
    return;
  }

  if (terms.length === 0) {
    panel.hidden = true;
    list.replaceChildren();
    return;
  }

  const searchUrl = window.Theme?.routes?.search_url || '/search';
  const fragment = document.createDocumentFragment();

  terms.forEach((term) => {
    const item = document.createElement('li');
    item.className = 'predictive-search-results__card--query';
    item.setAttribute('ref', 'resultsItems[]');

    const link = document.createElement('a');
    link.className = 'island-search-chip';
    link.href = `${searchUrl}?q=${encodeURIComponent(term)}`;
    link.dataset.searchTerm = term;
    link.textContent = term;

    item.appendChild(link);
    fragment.appendChild(item);
  });

  list.replaceChildren(fragment);
  panel.hidden = false;
}

/**
 * @param {HTMLInputElement} input
 * @param {string} term
 */
function fillAndSearch(input, term) {
  input.value = term;
  input.dispatchEvent(
    new InputEvent('input', {
      bubbles: true,
      composed: true,
      inputType: 'insertText',
      data: term,
    })
  );
  input.focus();
}

/**
 * Horizontal product-row scrolling: mouse drag, mouse wheel, trackpad, and touch.
 * @param {HTMLElement} modal
 */
function bindProductRowScroll(modal) {
  if (modal.dataset.islandSearchScrollReady === 'true') return;
  modal.dataset.islandSearchScrollReady = 'true';

  /** @type {{ scroller: HTMLElement, startX: number, startScroll: number, moved: boolean } | null} */
  let drag = null;
  let suppressClick = false;

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  function getScroller(target) {
    if (!(target instanceof Element)) return null;
    const scroller = target.closest('.island-search-product-scroll');
    return scroller instanceof HTMLElement && modal.contains(scroller) ? scroller : null;
  }

  modal.addEventListener(
    'wheel',
    (event) => {
      const scroller = getScroller(event.target);
      if (!scroller) return;
      if (scroller.scrollWidth <= scroller.clientWidth + 1) return;

      const usingVertical = Math.abs(event.deltaY) >= Math.abs(event.deltaX);
      const delta = usingVertical ? event.deltaY : event.deltaX;
      if (delta === 0) return;

      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      const next = scroller.scrollLeft + delta;
      const canScroll =
        (delta > 0 && scroller.scrollLeft < maxScroll - 1) || (delta < 0 && scroller.scrollLeft > 1);

      if (!canScroll) return;

      event.preventDefault();
      scroller.scrollLeft += delta;
    },
    { passive: false, capture: true }
  );

  modal.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const scroller = getScroller(event.target);
    if (!scroller || scroller.scrollWidth <= scroller.clientWidth + 1) return;

    drag = {
      scroller,
      startX: event.clientX,
      startScroll: scroller.scrollLeft,
      moved: false,
    };
    scroller.classList.add('is-dragging');
  });

  window.addEventListener('pointermove', (event) => {
    if (!drag) return;
    const dx = event.clientX - drag.startX;
    if (Math.abs(dx) > 6) drag.moved = true;
    drag.scroller.scrollLeft = drag.startScroll - dx;
  });

  window.addEventListener('pointerup', () => {
    if (!drag) return;
    drag.scroller.classList.remove('is-dragging');
    suppressClick = drag.moved;
    drag = null;
    if (suppressClick) {
      window.setTimeout(() => {
        suppressClick = false;
      }, 0);
    }
  });

  modal.addEventListener(
    'click',
    (event) => {
      if (!suppressClick) return;
      const scroller = getScroller(event.target);
      if (!scroller) return;
      event.preventDefault();
      event.stopPropagation();
    },
    true
  );
}

function initIslandSearch() {
  const modal = getModal();
  if (!modal || modal.dataset.islandSearchReady === 'true') return;
  modal.dataset.islandSearchReady = 'true';

  const results = modal.querySelector('[ref="predictiveSearchResults"]');
  const form = modal.querySelector('.predictive-search-form');
  const input = modal.querySelector('.search-input');

  renderRecentSearches(modal);
  bindProductRowScroll(modal);

  if (results) {
    const observer = new MutationObserver(() => {
      renderRecentSearches(modal);
    });
    observer.observe(results, { childList: true, subtree: true });
  }

  modal.addEventListener('click', (event) => {
    const chip = event.target instanceof Element ? event.target.closest('[data-search-term]') : null;
    if (!(chip instanceof HTMLElement) || !chip.dataset.searchTerm) return;
    if (!(input instanceof HTMLInputElement)) return;

    event.preventDefault();
    const term = chip.dataset.searchTerm;
    saveRecentSearch(term);
    fillAndSearch(input, term);
  });

  form?.addEventListener('submit', () => {
    if (input instanceof HTMLInputElement) {
      saveRecentSearch(input.value);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initIslandSearch, { once: true });
} else {
  initIslandSearch();
}
